import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuroCents } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Save, Plus, Trash2, ChefHat, Search } from "lucide-react";

const UNITS = ["gr", "kg", "ml", "lt", "τεμ"];
const field = "rounded-lg border border-[#E3DED4] px-2 py-1 text-sm outline-none focus:border-[#45423C]";

export default function RecipeBulkEdit() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState({});     // recipe_id -> {portions, menu_price, items[]}
  const [prices, setPrices] = useState({});   // ingredient_id -> price

  const { data: recipes } = useQuery({ queryKey: ["recipes", "", "all"], queryFn: async () => (await api.get("/recipes?q=&category=all")).data });
  const { data: ings } = useQuery({ queryKey: ["ingredients", ""], queryFn: async () => (await api.get("/recipes/ingredients")).data });

  const ingList = ings?.rows || [];
  const ingMap = useMemo(() => Object.fromEntries(ingList.map((i) => [i.id, i])), [ingList]);

  const [ingSearch, setIngSearch] = useState("");
  const [ingCat, setIngCat] = useState("all");
  const ingCats = useMemo(
    () => [...new Set(ingList.map((i) => i.category).filter(Boolean))].sort((a, b) => a.localeCompare(b, "el")),
    [ingList]
  );
  const filteredIngs = useMemo(() => {
    const q = ingSearch.trim().toLowerCase();
    return ingList.filter((i) =>
      (ingCat === "all" || i.category === ingCat) &&
      (!q || i.name.toLowerCase().includes(q))
    );
  }, [ingList, ingSearch, ingCat]);

  const rowOf = (rec) => draft[rec.id] || {
    portions: rec.portions || 1, menu_price: rec.menu_price || 0,
    items: (rec.items || []).map((i, ix) => ({ ...i, _key: i._key ?? `${i.ingredient_id || "it"}-${ix}` })),
  };
  const patch = (rec, upd) => setDraft({ ...draft, [rec.id]: { ...rowOf(rec), ...upd } });

  const priceOf = (id) => (prices[id] === undefined ? (ingMap[id]?.price_per_unit ?? 0) : prices[id]);

  const cost = (rec) => {
    const row = rowOf(rec);
    let total = 0;
    for (const it of row.items) {
      const ing = ingMap[it.ingredient_id];
      if (!ing) continue;
      const f = { gr: 0.001, kg: 1, ml: 0.001, lt: 1 };
      const q = Number(it.qty) || 0;
      const conv = it.unit === ing.unit ? q : (f[it.unit] && f[ing.unit] ? q * f[it.unit] / f[ing.unit] : null);
      if (conv === null) continue;
      total += conv * (Number(priceOf(ing.id)) || 0) * (1 + (Number(ing.waste_pct) || 0) / 100);
    }
    const per = total / Math.max(1, Number(row.portions) || 1);
    const price = Number(row.menu_price) || 0;
    return { per, pct: price ? (per / price) * 100 : null };
  };

  const save = useMutation({
    mutationFn: async () => (await api.put("/recipes/bulk", {
      recipes: Object.entries(draft).map(([id, r]) => ({
        id, portions: Number(r.portions) || 1, menu_price: Number(r.menu_price) || 0,
        items: r.items.filter((i) => i.ingredient_id).map((i) => ({
          ingredient_id: i.ingredient_id, qty: Number(i.qty) || 0, unit: i.unit || "gr", note: i.note || "",
        })),
      })),
      ingredients: Object.entries(prices).map(([id, p]) => ({ id, price_per_unit: Number(p) || 0 })),
    })).data,
    onSuccess: (d) => {
      setDraft({}); setPrices({});
      qc.invalidateQueries({ queryKey: ["recipes"] });
      qc.invalidateQueries({ queryKey: ["ingredients"] });
      toast.success(`Αποθηκεύτηκαν ${d.recipes} συνταγές και ${d.prices} τιμές`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const dirty = Object.keys(draft).length + Object.keys(prices).length;

  return (
    <div className="space-y-6" data-wide data-testid="recipe-bulk-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">F&amp;B · Διόρθωση με τον chef</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Μαζική Επεξεργασία Συνταγών</h1>
          <p className="text-[#6F6A60] text-sm mt-1">Πέρνα ποσότητες και πραγματικές τιμές υλικών σε μία οθόνη. Το food cost ενημερώνεται ζωντανά.</p>
        </div>
        <button data-testid="bulk-save" onClick={() => save.mutate()} disabled={!dirty || save.isPending}
          className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-40">
          <Save className="h-4 w-4" /> {save.isPending ? "Αποθήκευση…" : `Αποθήκευση όλων${dirty ? ` (${dirty})` : ""}`}
        </button>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="bulk-prices-card">
        <div className="border-b border-[#E3DED4]">
          <div className="px-5 py-4 flex flex-wrap items-center gap-3">
            <span className="font-medium text-[#45423C]">Τιμές υλικών</span>
            <span className="text-xs text-[#8A8577]" data-testid="bulk-ing-count">{filteredIngs.length} / {ingList.length}</span>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="h-4 w-4 text-[#6F6A60] absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input data-testid="bulk-ing-search" value={ingSearch} onChange={(e) => setIngSearch(e.target.value)}
                  placeholder="Αναζήτηση υλικού" className={`${field} pl-8 w-44 sm:w-56`} />
              </div>
              <select data-testid="bulk-ing-cat" value={ingCat} onChange={(e) => setIngCat(e.target.value)} className={`${field} max-w-[200px]`}>
                <option value="all">Όλες οι κατηγορίες</option>
                {ingCats.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="px-4 sm:px-5 pb-3 flex items-center gap-1.5 overflow-x-auto no-scrollbar" data-testid="bulk-ing-tabs">
            <button data-testid="bulk-ing-tab-all" onClick={() => setIngCat("all")}
              className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap transition-colors ${
                ingCat === "all" ? "bg-[#45423C] text-white" : "bg-[#F1EDE6] text-[#6F6A60] hover:bg-[#E7E1D7]"}`}>
              Όλες
            </button>
            {ingCats.map((c) => (
              <button key={c} data-testid={`bulk-ing-tab-${c}`} onClick={() => setIngCat(c)}
                className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap transition-colors ${
                  ingCat === c ? "bg-[#45423C] text-white" : "bg-[#F1EDE6] text-[#6F6A60] hover:bg-[#E7E1D7]"}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
        <div className="divide-y divide-[#F1EDE6] max-h-[55vh] overflow-y-auto">
          {filteredIngs.map((i) => (
            <div key={i.id} className="flex items-center gap-3 px-4 sm:px-5 py-1.5 hover:bg-[#FAF8F4]" data-testid={`bulk-ing-row-${i.id}`}>
              <span className="text-sm text-[#45423C] flex-1 min-w-0 truncate" title={i.name}>{i.name}</span>
              {i.category ? <span className="hidden md:inline text-[10px] uppercase tracking-wider text-[#8A8577] whitespace-nowrap">{i.category}</span> : null}
              <span className="text-xs text-[#8A8577] whitespace-nowrap">€/{i.unit}</span>
              <input data-testid={`bulk-price-${i.id}`} type="number" step="0.01" value={priceOf(i.id)}
                onChange={(e) => setPrices({ ...prices, [i.id]: e.target.value })}
                className={`${field} w-24 text-right tabular`} />
            </div>
          ))}
          {filteredIngs.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-[#6F6A60]" data-testid="bulk-ing-empty">Κανένα υλικό με αυτά τα κριτήρια.</div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {(recipes?.rows || []).map((rec) => {
          const row = rowOf(rec);
          const c = cost(rec);
          return (
            <div key={rec.id} className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid={`bulk-recipe-${rec.id}`}>
              <div className="px-5 py-3 border-b border-[#E3DED4] flex flex-wrap items-center gap-3">
                <ChefHat className="h-4 w-4 text-[#C86235]" />
                <span className="font-medium text-[#45423C]">{rec.name}</span>
                <span className="text-xs text-[#8A8577]">{rec.category}</span>
                <div className="ml-auto flex flex-wrap items-center gap-3 text-xs">
                  <label className="flex items-center gap-1.5">Μερίδες
                    <input data-testid={`bulk-portions-${rec.id}`} type="number" value={row.portions}
                      onChange={(e) => patch(rec, { portions: e.target.value })} className={`${field} w-16 text-right tabular`} />
                  </label>
                  <label className="flex items-center gap-1.5">Τιμή μενού €
                    <input data-testid={`bulk-menu-price-${rec.id}`} type="number" step="0.01" value={row.menu_price}
                      onChange={(e) => patch(rec, { menu_price: e.target.value })} className={`${field} w-20 text-right tabular`} />
                  </label>
                  <span className="rounded-full bg-[#F1EDE6] px-2.5 py-1 font-semibold tabular" data-testid={`bulk-cost-${rec.id}`}>
                    {fmtEuroCents(c.per)} / μερίδα
                  </span>
                  <span className={`rounded-full px-2.5 py-1 font-semibold ${
                    c.pct === null ? "bg-[#F1EDE6] text-[#45423C]" : c.pct <= 30 ? "bg-[#EDF2E8] text-[#4A5A3E]" : c.pct <= 38 ? "bg-[#FBF3E4] text-[#8A6A22]" : "bg-[#FEF2F2] text-[#991B1B]"}`}
                    data-testid={`bulk-fc-${rec.id}`}>
                    {c.pct === null ? "χωρίς τιμή" : `food cost ${c.pct.toFixed(1)}%`}
                  </span>
                </div>
              </div>
              <div className="p-4 space-y-2">
                {row.items.map((it, idx) => (
                  <div key={it._key ?? idx} className="flex flex-wrap items-center gap-2">
                    <select data-testid={`bulk-item-${rec.id}-${idx}`} value={it.ingredient_id}
                      onChange={(e) => {
                        const items = [...row.items];
                        items[idx] = { ...it, ingredient_id: e.target.value };
                        patch(rec, { items });
                      }} className={`${field} flex-1 min-w-[180px]`}>
                      <option value="">— υλικό —</option>
                      {ingList.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </select>
                    <input data-testid={`bulk-qty-${rec.id}-${idx}`} type="number" step="0.01" value={it.qty}
                      onChange={(e) => {
                        const items = [...row.items];
                        items[idx] = { ...it, qty: e.target.value };
                        patch(rec, { items });
                      }} className={`${field} w-24 text-right tabular`} />
                    <select data-testid={`bulk-unit-${rec.id}-${idx}`} value={it.unit}
                      onChange={(e) => {
                        const items = [...row.items];
                        items[idx] = { ...it, unit: e.target.value };
                        patch(rec, { items });
                      }} className={`${field} w-20`}>
                      {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <button data-testid={`bulk-remove-${rec.id}-${idx}`}
                      onClick={() => patch(rec, { items: row.items.filter((_x, i2) => i2 !== idx) })}
                      className="p-1.5 rounded-lg text-[#991B1B] hover:bg-[#FEF2F2]"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <button data-testid={`bulk-add-${rec.id}`}
                  onClick={() => patch(rec, { items: [...row.items, { ingredient_id: "", qty: 0, unit: "gr", note: "", _key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `new-${Date.now()}-${row.items.length}` }] })}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-[#E3DED4] px-3 py-1.5 text-xs font-semibold hover:border-[#B9AE9A]">
                  <Plus className="h-3.5 w-3.5" /> Υλικό
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
